import React, { useState, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";
import { X } from "lucide-react";
import { addEmailToCartMailChimp, apiMail } from "../actions/userActions";
import { ContactResponse } from "../emails/new";

const PopupOffer = ({ show, setShow, force, setForce }) => {
	const [email, setEmail] = useState("");
	const [name, setName] = useState("");
	const [showForm, setShowForm] = useState(false);
	const isMobile = useMediaQuery({ query: "(max-width: 767px)" });

	const handleClose = () => {
		localStorage.setItem("showPopup", new Date().getTime());
		setShow(false);
		setForce(false);
	};

	const checkTime = () => {
		if (force) {
			setShow(true);
			return false;
		}
		const storedTime = localStorage.getItem("showPopup");
		if (!storedTime) return false;
		const daysDifference =
			(new Date().getTime() - storedTime) / (1000 * 3600 * 24);
		return daysDifference < 7;
	};

	function splitFullName(fullName) {
		const nameRegex = /^(\S+)\s+(.+)$/;
		const match = fullName.trim().match(nameRegex);

		if (match) {
			return {
				fname: match[1],
				lname: match[2],
			};
		} else {
			return {
				fname: fullName,
				lname: "-",
			};
		}
	}

	const sendGiftCard = async (e) => {
		e.preventDefault();
		try {
			alert("Thank you! We'll send you a promo code shortly via email.");
			await addEmailToCartMailChimp(email, "New Promo 10%", name, "-");
			await apiMail(
				email,
				`10% OFF PROMO CODE FROM OFF CUT SHADES`,
				<ContactResponse
					name={splitFullName(name)["fname"]}
					promo={true}
					promoCode={"10PROMO"}
				/>
			);
			handleClose();
		} catch (error) {
			console.log("Error sending gift card:", error);
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "An unexpected error occurred. Please contact info@offcutshades.com.",
			});
		}
	};

	// Show the offer once the visitor has engaged (scrolled ~40% of the page)
	// or after 20s, instead of covering the hero 2s after landing.
	useEffect(() => {
		if (force) {
			setShow(true);
			return undefined;
		}
		if (checkTime()) return undefined;

		let opened = false;
		let timer = null;
		const onScroll = () => {
			const max =
				document.documentElement.scrollHeight - window.innerHeight;
			if (max > 0 && window.scrollY / max > 0.4) open();
		};
		const cleanup = () => {
			if (timer) clearTimeout(timer);
			window.removeEventListener("scroll", onScroll);
		};
		const open = () => {
			if (opened) return;
			opened = true;
			cleanup();
			setShow(true);
		};

		timer = setTimeout(open, 20000);
		window.addEventListener("scroll", onScroll, { passive: true });
		return cleanup;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [force]);

	return (
		<div className="w-full">
			{show && (
				// Same look as the rest of the site: off-white panel, ink type,
				// serif heading, square corners, black button
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-[#141414]/50 p-4"
					onClick={handleClose}
				>
					<div
						role="dialog"
						aria-modal="true"
						aria-labelledby="popup-offer-title"
						onClick={(e) => e.stopPropagation()}
						className={`relative flex w-full max-w-[880px] max-h-[92vh] overflow-y-auto bg-[#FAFAF7] text-[#141414] font-inter ${
							isMobile ? "flex-col" : "flex-row"
						}`}
					>
						<div
							className={`bg-[#F1EFEA] ${
								isMobile ? "h-[200px] w-full" : "w-1/2 min-h-[480px]"
							}`}
						>
							<img
								src="/images/popup_left.webp"
								width={668}
								height={751}
								className="w-full h-full object-cover"
								alt=""
							/>
						</div>
						<div className="relative flex flex-col justify-center px-8 py-10 md:px-12 w-full md:w-1/2">
							<button
								type="button"
								aria-label="Close offer"
								className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-[#5E5E5A] hover:text-[#141414] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414]"
								onClick={handleClose}
							>
								<X strokeWidth={1.5} className="w-5 h-5" aria-hidden="true" />
							</button>
							<p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#5E5E5A]">
								A welcome offer
							</p>
							<h2
								id="popup-offer-title"
								className="mt-3 font-instrument text-[44px] md:text-[56px] font-normal leading-[0.95]"
							>
								An extra 10% off
							</h2>
							<p className="mt-4 text-[15px] text-[#5E5E5A]">
								Get your shades for an even better price. We'll email you
								a promo code to use at checkout.
							</p>
							{!showForm ? (
								<div className="mt-8 flex flex-col items-start gap-4">
									<button
										type="button"
										className="min-h-[52px] px-8 bg-[#141414] text-[#FAFAF7] text-sm font-medium"
										onClick={() => setShowForm(true)}
									>
										Claim your 10% off code
									</button>
									<button
										type="button"
										className="text-[13px] text-[#5E5E5A] underline underline-offset-4 decoration-1 hover:text-[#141414]"
										onClick={handleClose}
									>
										No thanks
									</button>
								</div>
							) : (
								<form
									className="mt-8 flex flex-col w-full space-y-4"
									onSubmit={sendGiftCard}
								>
									<input
										className="w-full border border-[#D6D3CC] bg-transparent px-4 py-3 text-[15px] rounded-none focus:outline-none focus:border-[#141414] focus:ring-0"
										type="text"
										placeholder="Name"
										aria-label="Name"
										required
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
									<input
										className="w-full border border-[#D6D3CC] bg-transparent px-4 py-3 text-[15px] rounded-none focus:outline-none focus:border-[#141414] focus:ring-0"
										type="email"
										placeholder="Email"
										aria-label="Email"
										required
										value={email}
										onChange={(e) => setEmail(e.target.value)}
									/>
									<button
										className="min-h-[52px] bg-[#141414] text-[#FAFAF7] text-sm font-medium"
										type="submit"
									>
										Send my code
									</button>
								</form>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PopupOffer;
